import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import PageHero from '../../components/ui/page-hero';
import Footer from '../../components/ui/footer';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Shield, 
  Eye, 
  Lock, 
  Database, 
  Mail, 
  Phone, 
  Calendar,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  FileText,
  Users,
  Globe,
  Server,
  UserCheck,
  Settings,
  Download,
  Trash2,
  Edit,
  Building2,
  MapPin,
  Clock
} from 'lucide-react';

const PrivacyPolicy = () => {
  const lastUpdated = "January 1, 2024";
  const effectiveDate = "January 1, 2024";
  const [activeSection, setActiveSection] = useState('');

  // Navigation sections
  const navigationSections = useMemo(() => [
    { id: 'privacy-principles', title: 'Privacy Principles', icon: Shield },
    { id: 'information-collection', title: 'Information We Collect', icon: Database },
    { id: 'data-usage', title: 'How We Use Your Data', icon: Globe },
    { id: 'information-sharing', title: 'Information Sharing', icon: Users },
    { id: 'your-rights', title: 'Your Privacy Rights', icon: UserCheck },
    { id: 'security-measures', title: 'Security Measures', icon: Lock },
    { id: 'cookies-tracking', title: 'Cookies & Tracking', icon: Settings },
    { id: 'data-retention', title: 'Data Retention', icon: Clock },
    { id: 'international-transfers', title: 'International Transfers', icon: Globe },
    { id: 'children-privacy', title: "Children's Privacy", icon: Users },
    { id: 'policy-updates', title: 'Policy Updates', icon: FileText },
    { id: 'contact-information', title: 'Contact Information', icon: Mail }
  ], []);

  // Scroll spy functionality
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, observerOptions);

    navigationSections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [navigationSections]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const privacyPrinciples = [
    {
      icon: Shield,
      title: 'Data Protection',
      description: 'We implement industry-standard security measures to protect your personal information from unauthorized access, alteration, or disclosure.'
    },
    {
      icon: Eye,
      title: 'Transparency',
      description: 'We clearly explain how we collect, use, and share your data, ensuring you understand our practices and your rights.'
    },
    {
      icon: Lock,
      title: 'Minimal Collection',
      description: 'We only collect the data necessary to provide our services effectively and improve your experience.'
    },
    {
      icon: Database,
      title: 'Secure Storage',
      description: 'Your data is stored securely using modern encryption protocols and access controls.'
    }
  ];

  const dataTypes = [
    {
      category: 'Personal Information',
      icon: Users,
      description: 'Information that directly identifies you as an individual',
      items: [
        'Full name and contact information (email, phone number, address)',
        'Company name, job title, and professional information',
        'Communication preferences and language settings',
        'Account credentials and authentication information',
        'Profile pictures and biographical information'
      ]
    },
    {
      category: 'Project Information',
      icon: FileText,
      description: 'Information related to your projects and business requirements',
      items: [
        'Project requirements, specifications, and technical details',
        'Business goals, objectives, and success metrics',
        'Budget information and payment details',
        'Timeline requirements and project milestones',
        'Technical constraints and integration requirements'
      ]
    },
    {
      category: 'Usage Data',
      icon: Server,
      description: 'Information about how you interact with our services',
      items: [
        'Website usage patterns, page views, and session data',
        'Communication history and support interactions',
        'Service usage statistics and feature utilization',
        'Device information, browser type, and IP address',
        'Cookies and similar tracking technologies'
      ]
    },
    {
      category: 'Technical Data',
      icon: Settings,
      description: 'Technical information necessary for service delivery',
      items: [
        'System logs and error reports',
        'Performance metrics and analytics data',
        'Integration data and API usage',
        'Backup and recovery information',
        'Security logs and access records'
      ]
    }
  ];

  const dataUsage = [
    {
      purpose: 'Service Delivery',
      icon: Globe,
      description: 'To provide and improve our web development, AI, and digital transformation services',
      details: [
        'Developing and customizing solutions according to your requirements',
        'Providing technical support and maintenance services',
        'Managing project timelines and deliverables',
        'Ensuring service quality and performance optimization'
      ]
    },
    {
      purpose: 'Communication',
      icon: Mail,
      description: 'To respond to inquiries, provide support, and send important updates',
      details: [
        'Responding to your questions and support requests',
        'Sending project updates and status notifications',
        'Providing important service announcements',
        'Facilitating team collaboration and communication'
      ]
    },
    {
      purpose: 'Project Management',
      icon: FileText,
      description: 'To manage projects, track progress, and ensure successful delivery',
      details: [
        'Creating and maintaining project documentation',
        'Tracking milestones and deliverables',
        'Managing team assignments and resource allocation',
        'Generating progress reports and analytics'
      ]
    },
    {
      purpose: 'Business Operations',
      icon: Building2,
      description: 'To process payments, maintain records, and comply with legal obligations',
      details: [
        'Processing payments and managing billing',
        'Maintaining business records and documentation',
        'Complying with tax and regulatory requirements',
        'Conducting business analytics and reporting'
      ]
    },
    {
      purpose: 'Security & Compliance',
      icon: Shield,
      description: 'To protect our services, users, and comply with legal requirements',
      details: [
        'Monitoring for security threats and vulnerabilities',
        'Investigating and preventing fraud or abuse',
        'Complying with legal and regulatory requirements',
        'Maintaining audit trails and compliance records'
      ]
    }
  ];

  const dataSharing = [
    {
      category: 'Service Providers',
      icon: Server,
      description: 'We may share data with trusted third-party service providers who assist in delivering our services',
      examples: [
        'Cloud hosting providers (AWS, Google Cloud, Microsoft Azure)',
        'Payment processors (Stripe, PayPal)',
        'Email service providers (SendGrid, Mailchimp)',
        'Analytics providers (Google Analytics, Mixpanel)',
        'Customer support platforms (Zendesk, Intercom)'
      ],
      safeguards: 'All service providers are bound by strict confidentiality agreements, data protection requirements, and are regularly audited for compliance.'
    },
    {
      category: 'Legal Requirements',
      icon: FileText,
      description: 'We may disclose data when required by law, court order, or to protect our rights and the safety of our users',
      examples: [
        'Compliance with court orders or legal subpoenas',
        'Cooperation with law enforcement investigations',
        'Protection of our legal rights and property',
        'Prevention of fraud or illegal activities'
      ],
      safeguards: 'We only share the minimum amount of data necessary to comply with legal obligations and always seek to protect user privacy.'
    },
    {
      category: 'Business Transfers',
      icon: Building2,
      description: 'In the event of a merger, acquisition, or sale of assets, data may be transferred as part of the business transaction',
      examples: [
        'Merger with another company',
        'Acquisition by a larger organization',
        'Sale of business assets or divisions',
        'Corporate restructuring or reorganization'
      ],
      safeguards: 'Any such transfer will be subject to the same privacy protections outlined in this policy, and users will be notified of significant changes.'
    },
    {
      category: 'Consent-Based Sharing',
      icon: UserCheck,
      description: 'We may share data with your explicit consent for specific purposes',
      examples: [
        'Integration with third-party tools you request',
        'Sharing testimonials or case studies (with permission)',
        'Participation in industry surveys or research',
        'Collaboration with partners you approve'
      ],
      safeguards: 'We only share data when you have given explicit, informed consent and you can withdraw this consent at any time.'
    }
  ];

  const userRights = [
    {
      right: 'Access',
      icon: Eye,
      description: 'Request access to the personal data we hold about you',
      details: 'You can request a copy of all personal data we have collected about you, including how it was obtained and how it is being used.'
    },
    {
      right: 'Correction',
      icon: Edit,
      description: 'Request correction of inaccurate or incomplete personal data',
      details: 'You can ask us to correct any inaccurate or incomplete information we hold about you.'
    },
    {
      right: 'Deletion',
      icon: Trash2,
      description: 'Request deletion of your personal data (subject to legal and contractual obligations)',
      details: 'You can request that we delete your personal data, though we may need to retain some information for legal or business purposes.'
    },
    {
      right: 'Portability',
      icon: Download,
      description: 'Request a copy of your data in a structured, machine-readable format',
      details: 'You can request that we provide your data in a format that allows you to transfer it to another service provider.'
    },
    {
      right: 'Restriction',
      icon: Lock,
      description: 'Request restriction of processing of your personal data',
      details: 'You can ask us to limit how we use your data in certain circumstances.'
    },
    {
      right: 'Objection',
      icon: AlertTriangle,
      description: 'Object to processing of your personal data for certain purposes',
      details: 'You can object to certain types of data processing, such as marketing communications or automated decision-making.'
    }
  ];

  const securityMeasures = [
    {
      measure: 'Encryption',
      description: 'All data is encrypted both in transit and at rest using industry-standard encryption protocols',
      details: ['AES-256 encryption for data at rest', 'TLS 1.3 for data in transit', 'End-to-end encryption for sensitive communications']
    },
    {
      measure: 'Access Controls',
      description: 'Strict access controls and authentication mechanisms to prevent unauthorized access',
      details: ['Multi-factor authentication for all accounts', 'Role-based access controls', 'Regular access reviews and audits']
    },
    {
      measure: 'Security Monitoring',
      description: 'Continuous monitoring and threat detection to identify and respond to security incidents',
      details: ['24/7 security monitoring', 'Automated threat detection', 'Incident response procedures']
    },
    {
      measure: 'Regular Audits',
      description: 'Regular security audits and vulnerability assessments to ensure ongoing protection',
      details: ['Quarterly security assessments', 'Penetration testing', 'Compliance audits']
    },
    {
      measure: 'Employee Training',
      description: 'Comprehensive training programs to ensure all employees understand data protection requirements',
      details: ['Data protection training for all staff', 'Security awareness programs', 'Regular policy updates and training']
    },
    {
      measure: 'Backup & Recovery',
      description: 'Robust backup and disaster recovery procedures to protect against data loss',
      details: ['Automated daily backups', 'Geographically distributed storage', 'Tested recovery procedures']
    }
  ];

  const cookies = [
    {
      type: 'Essential Cookies',
      purpose: 'Required for basic website functionality',
      examples: ['Authentication cookies', 'Session management', 'Security tokens'],
      retention: 'Session-based or up to 1 year'
    },
    {
      type: 'Analytics Cookies',
      purpose: 'Help us understand how visitors interact with our website',
      examples: ['Google Analytics', 'Page view tracking', 'User behavior analysis'],
      retention: 'Up to 2 years'
    },
    {
      type: 'Functional Cookies',
      purpose: 'Enable enhanced functionality and personalization',
      examples: ['Language preferences', 'Theme settings', 'User preferences'],
      retention: 'Up to 1 year'
    },
    {
      type: 'Marketing Cookies',
      purpose: 'Used to deliver relevant advertisements and track campaign performance',
      examples: ['Social media pixels', 'Advertising networks', 'Conversion tracking'],
      retention: 'Up to 1 year'
    }
  ];

  const dataRetention = [
    {
      dataType: 'Account Information',
      retentionPeriod: 'As long as your account is active, plus 3 years after closure',
      reason: 'To provide ongoing service and comply with legal requirements'
    },
    {
      dataType: 'Project Data',
      retentionPeriod: 'Duration of project plus 7 years',
      reason: 'To maintain project records and provide ongoing support'
    },
    {
      dataType: 'Communication Records',
      retentionPeriod: '3 years from last contact',
      reason: 'To maintain service quality and resolve disputes'
    },
    {
      dataType: 'Payment Information',
      retentionPeriod: '7 years after last transaction',
      reason: 'To comply with tax and accounting requirements'
    },
    {
      dataType: 'Website Analytics',
      retentionPeriod: '2 years from collection',
      reason: 'To analyze trends and improve our services'
    }
  ];

  const internationalTransfers = [
    {
      destination: 'United States',
      purpose: 'Cloud hosting and service delivery',
      safeguards: 'Standard Contractual Clauses and adequacy decisions'
    },
    {
      destination: 'European Union',
      purpose: 'Data processing and customer support',
      safeguards: 'Adequacy decision and GDPR compliance'
    },
    {
      destination: 'Morocco',
      purpose: 'Local data processing and storage',
      safeguards: 'Local data protection laws and regulations'
    }
  ];

  const childrenPrivacy = {
    ageLimit: 16,
    description: 'We do not knowingly collect personal information from children under 16 years of age. If we become aware that we have collected personal information from a child under 16, we will take steps to delete such information promptly.',
    parentalRights: [
      'Right to review and delete their child\'s information',
      'Right to refuse further collection of their child\'s information',
      'Right to consent to collection and use of their child\'s information'
    ]
  };

  const updatesPolicy = {
    notification: 'We will notify you of any material changes to this Privacy Policy by email or through our website',
    effectiveDate: 'Changes will become effective 30 days after notification',
    versionControl: 'We maintain a version history of all policy changes',
    consent: 'Continued use of our services after changes constitutes acceptance of the updated policy'
  };

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Envael - Your Privacy Matters</title>
        <meta name="description" content="Learn how Envael protects your privacy and personal data. Our comprehensive privacy policy explains our data collection, usage, and protection practices." />
        <meta name="keywords" content="privacy policy, data protection, GDPR compliance, Morocco privacy, Envael privacy, data security" />
        <meta property="og:title" content="Privacy Policy | Envael - Your Privacy Matters" />
        <meta property="og:description" content="Learn how Envael protects your privacy and personal data with industry-standard security measures." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy | Envael" />
        <meta name="twitter:description" content="Your privacy matters. Learn how we protect your data." />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <PageHero
          pageName="Privacy Policy"
          subtitle="Your privacy matters to us. Learn how we collect, use, and protect your personal information with industry-standard security measures and transparent practices."
          size="large"
          fullHeight={true}
          enableSpline={true}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center">
          <p className="text-sm text-muted-foreground">
            Last updated <strong>{lastUpdated}</strong> • Effective <strong>{effectiveDate}</strong>
          </p>
        </div>

        {/* Mobile Navigation */}
        <div className="lg:hidden bg-background border-b border-border sticky top-16 z-40">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex overflow-x-auto space-x-4">
              {navigationSections.map((section) => {
                const IconComponent = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`flex-shrink-0 px-3 py-2 rounded-md text-xs font-medium transition-colors flex items-center space-x-2 ${
                      activeSection === section.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-accent'
                    }`}
                  >
                    <IconComponent className="h-3 w-3" />
                    <span className="whitespace-nowrap">{section.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main Content with Sidebar */}
        <div className="flex max-w-7xl mx-auto">
          {/* Sidebar Navigation */}
          <div className="hidden lg:block w-80 p-6 sticky top-24 h-fit">
            <div className="bg-muted/30 rounded-lg p-6 border border-border">
              <h3 className="text-lg font-semibold text-foreground mb-4">Table of Contents</h3>
              <nav className="space-y-2">
                {navigationSections.map((section) => {
                  const IconComponent = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center space-x-3 ${
                        activeSection === section.id
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                      }`}
                    >
                      <IconComponent className="h-4 w-4 flex-shrink-0" />
                      <span>{section.title}</span>
                    </button>
                  );
                })}
              </nav>
              
              {/* Back to Top */}
              <div className="mt-6 pt-4 border-t border-border">
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="w-full text-center px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                >
                  ↑ Back to Top
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6">

        {/* Privacy Principles */}
        <section id="privacy-principles" className="py-12 bg-muted/30 mb-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Our Privacy Principles
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We are committed to protecting your privacy and maintaining the highest standards of data protection.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {privacyPrinciples.map((principle, index) => (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <principle.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {principle.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {principle.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
          </div>
        </section>

        {/* Information We Collect */}
        <section id="information-collection" className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Information We Collect
                </h2>
                <p className="text-lg text-muted-foreground">
                  We collect only the information necessary to provide our services effectively and improve your experience.
                </p>
              </div>

              <div className="space-y-8">
                {dataTypes.map((type, index) => (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4 mb-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <type.icon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-2">
                            {type.category}
                          </h3>
                          <p className="text-muted-foreground">
                            {type.description}
                          </p>
                        </div>
                      </div>
                      <ul className="space-y-2 ml-14">
                        {type.items.map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-muted-foreground">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* How We Use Your Information */}
        <section id="data-usage" className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  How We Use Your Information
                </h2>
                <p className="text-lg text-muted-foreground">
                  We use your information solely for legitimate business purposes and to provide you with the best possible service.
                </p>
              </div>

              <div className="space-y-8">
                {dataUsage.map((usage, index) => (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4 mb-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <usage.icon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-2">
                            {usage.purpose}
                          </h3>
                          <p className="text-muted-foreground">
                            {usage.description}
                          </p>
                        </div>
                      </div>
                      <ul className="space-y-2 ml-14">
                        {usage.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start">
                            <ArrowRight className="h-4 w-4 text-primary mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-muted-foreground">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Information Sharing */}
        <section id="information-sharing" className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Information Sharing
                </h2>
                <p className="text-lg text-muted-foreground">
                  We do not sell your personal information. We only share data in limited circumstances with appropriate safeguards.
                </p>
              </div>

              <div className="space-y-8">
                {dataSharing.map((sharing, index) => (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4 mb-4">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <sharing.icon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-foreground mb-2">
                            {sharing.category}
                          </h3>
                          <p className="text-muted-foreground mb-4">
                            {sharing.description}
                          </p>
                        </div>
                      </div>
                      
                      <div className="ml-14 space-y-4">
                        <div>
                          <h4 className="font-semibold text-foreground mb-2">Examples:</h4>
                          <ul className="space-y-1">
                            {sharing.examples.map((example, idx) => (
                              <li key={idx} className="flex items-start">
                                <span className="w-2 h-2 bg-primary rounded-full mr-3 mt-2 flex-shrink-0"></span>
                                <span className="text-muted-foreground">{example}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div className="bg-primary/5 p-4 rounded-lg">
                          <h4 className="font-semibold text-foreground mb-2 flex items-center">
                            <Shield className="h-4 w-4 text-primary mr-2" />
                            Safeguards
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {sharing.safeguards}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Your Rights */}
        <section id="your-rights" className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Your Privacy Rights
                </h2>
                <p className="text-lg text-muted-foreground">
                  You have important rights regarding your personal data. We are committed to helping you exercise these rights.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {userRights.map((right, index) => (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-3 mb-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <right.icon className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-foreground mb-1">
                            {right.right}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-2">
                            {right.description}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {right.details}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="text-center">
                <Card className="bg-primary/5 border-primary/20">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-3">
                      How to Exercise Your Rights
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      To exercise any of your privacy rights, please contact us at privacy@envael.com or use our contact form.
                      We will respond to your request within 30 days.
                    </p>
                    <Button asChild>
                      <a href="/contact">
                        <Mail className="mr-2 h-4 w-4" />
                        Contact Us
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Security Measures */}
        <section id="security-measures" className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Security Measures
                </h2>
                <p className="text-lg text-muted-foreground">
                  We implement comprehensive security measures to protect your data from unauthorized access, alteration, or disclosure.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {securityMeasures.map((measure, index) => (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {measure.measure}
                      </h3>
                      <p className="text-muted-foreground mb-3">
                        {measure.description}
                      </p>
                      <ul className="space-y-1">
                        {measure.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-muted-foreground">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Cookies and Tracking */}
        <section id="cookies-tracking" className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Cookies and Tracking Technologies
                </h2>
                <p className="text-lg text-muted-foreground">
                  We use cookies and similar technologies to enhance your experience and analyze website usage.
                </p>
              </div>

              <div className="space-y-6">
                {cookies.map((cookie, index) => (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-foreground">
                          {cookie.type}
                        </h3>
                        <span className="text-sm bg-primary/10 text-primary px-3 py-1 rounded-full">
                          {cookie.retention}
                        </span>
                      </div>
                      <p className="text-muted-foreground mb-3">
                        {cookie.purpose}
                      </p>
                      <div>
                        <h4 className="font-semibold text-foreground mb-2">Examples:</h4>
                        <div className="flex flex-wrap gap-2">
                          {cookie.examples.map((example, idx) => (
                            <span key={idx} className="text-sm bg-muted px-3 py-1 rounded-full">
                              {example}
                            </span>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Data Retention */}
        <section id="data-retention" className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Data Retention
                </h2>
                <p className="text-lg text-muted-foreground">
                  We retain your personal data only for as long as necessary to fulfill the purposes outlined in this policy.
                </p>
              </div>

              <Card>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {dataRetention.map((retention, index) => (
                      <div key={index} className="flex items-start space-x-4 p-4 bg-muted/30 rounded-lg">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Clock className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-foreground mb-1">
                            {retention.dataType}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-2">
                            <strong>Retention Period:</strong> {retention.retentionPeriod}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            <strong>Reason:</strong> {retention.reason}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* International Transfers */}
        <section id="international-transfers" className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  International Data Transfers
                </h2>
                <p className="text-lg text-muted-foreground">
                  Your data may be transferred to and processed in countries other than your own.
                </p>
              </div>

              <div className="space-y-4">
                {internationalTransfers.map((transfer, index) => (
                  <Card key={index}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-foreground">
                          {transfer.destination}
                        </h3>
                        <span className="text-sm bg-primary/10 text-primary px-3 py-1 rounded-full">
                          {transfer.safeguards}
                        </span>
                      </div>
                      <p className="text-muted-foreground">
                        <strong>Purpose:</strong> {transfer.purpose}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Children's Privacy */}
        <section id="children-privacy" className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Children's Privacy
                </h2>
                <p className="text-lg text-muted-foreground">
                  We are committed to protecting the privacy of children.
                </p>
              </div>

              <Card>
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      Age Limit: {childrenPrivacy.ageLimit} Years
                    </h3>
                    <p className="text-muted-foreground">
                      {childrenPrivacy.description}
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-foreground mb-3">Parental Rights:</h4>
                    <ul className="space-y-2">
                      {childrenPrivacy.parentalRights.map((right, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{right}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Policy Updates */}
        <section id="policy-updates" className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Policy Updates
                </h2>
                <p className="text-lg text-muted-foreground">
                  We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements.
                </p>
              </div>

              <Card>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <Mail className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">Notification</h3>
                        <p className="text-muted-foreground">{updatesPolicy.notification}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Calendar className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">Effective Date</h3>
                        <p className="text-muted-foreground">{updatesPolicy.effectiveDate}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <FileText className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">Version Control</h3>
                        <p className="text-muted-foreground">{updatesPolicy.versionControl}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <UserCheck className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">Consent</h3>
                        <p className="text-muted-foreground">{updatesPolicy.consent}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section id="contact-information" className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Questions About Privacy?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                If you have any questions about this Privacy Policy or our data practices, please don't hesitate to contact us.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <Card>
                  <CardContent className="p-6 text-center">
                    <Mail className="h-8 w-8 text-primary mx-auto mb-3" />
                    <h3 className="font-semibold text-foreground mb-2">Email</h3>
                    <p className="text-sm text-muted-foreground">privacy@envael.com</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 text-center">
                    <Phone className="h-8 w-8 text-primary mx-auto mb-3" />
                    <h3 className="font-semibold text-foreground mb-2">Phone</h3>
                    <p className="text-sm text-muted-foreground">+212 6 63 08 13 83</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 text-center">
                    <MapPin className="h-8 w-8 text-primary mx-auto mb-3" />
                    <h3 className="font-semibold text-foreground mb-2">Address</h3>
                    <p className="text-sm text-muted-foreground">287 BLOC K APPT 2 ETAGE 1 OULED OUJIH KENITRA</p>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-primary/5 p-6 rounded-lg mb-8">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Data Protection Officer
                </h3>
                <p className="text-muted-foreground mb-4">
                  For privacy-related concerns, you can also contact our Data Protection Officer directly.
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>Email:</strong> contact@envael.com | <strong>Phone:</strong> +212 6 63 08 13 83
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild>
                  <a href="/contact">
                    <Mail className="mr-2 h-4 w-4" />
                    Contact Us
                  </a>
                </Button>
                <Button variant="outline" asChild>
                  <a href="/terms-of-service">
                    <FileText className="mr-2 h-4 w-4" />
                    Terms of Service
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </>
  );
};

export default PrivacyPolicy;